import React, { useEffect, useState } from "react";
import { Table, Spin } from "antd";
import dayjs from "dayjs";
import Instance from "../../../AxiosConfig";
import "./audits.css";

const AuditsTable = ({ companyData }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);

  const certificationIds = companyData?.certificationIds || [];

  useEffect(() => {
    if (certificationIds.length) {
      getAllCertificationsDetails();
    }
  }, [certificationIds]);

  const getAllCertificationsDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const promises = certificationIds.map((c) =>
        Instance.get(`Certification/get/${c._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      );

      const results = await Promise.all(promises);

      const formatted = results.map((res) => ({
        certificate: res.data.certification,
        pastCycles: res.data.pastCycleRecords || [],
      }));

      setCertificates(formatted);
    } catch (err) {
      console.error("Error loading certifications:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Badge for status color
  const StatusBadge = ({ status }) => {
    const s = status?.toLowerCase();
    const bg =
      s === "completed"
        ? "#DFF6E4"
        : s === "pending"
        ? "#FFF4E5"
        : "#FDEAEA";

    const color =
      s === "completed"
        ? "#0CBC53"
        : s === "pending"
        ? "#FFA500"
        : "#FF4D4F";

    return (
      <span
        style={{
          backgroundColor: bg,
          color,
          padding: "4px 10px",
          borderRadius: "12px",
          fontWeight: 500,
          display: "inline-block",
          minWidth: "70px",
          textAlign: "center",
        }}
      >
        {status || "-"}
      </span>
    );
  };

  // ✅ Reusable Audit Table Component
  const AuditSection = ({ title, audits }) => (
    <div style={{ marginBottom: 20 }}>
      <h4 style={{ fontSize: "16px", color: "#127285", fontWeight: "600" }}>
        {title}
      </h4>
      <Table
        size="small"
        bordered
        pagination={false}
        columns={[
          { title: "Audit Type", dataIndex: "auditType" },
          { title: "Audit Date", dataIndex: "date" },
          {
            title: "Status",
            dataIndex: "status",
            render: (v) => <StatusBadge status={v} />,
          },
          { title: "Auditor Notes", dataIndex: "notes" },
        ]}
        dataSource={audits}
      />
    </div>
  );

  // ✅ Prepare rows for main table
  const tableRows = [];

  certificates.forEach((item, idx) => {
    const current = item.certificate;
    const past = item.pastCycles;

    // ▶️ Active / Current Certification
    tableRows.push({
      key: `active-${idx}`,
      cycleType: `Certification ${idx + 1} (Active)`,
      certificateNo: current.certificationNumber,
      company: current.companyName,
      registrationDate: current.dateOfRegistration,
      expiryDate: current.certificationExpiryDate,
      type: "active",
      data: current,
    });

    // ▶️ Past Cycles
    past.forEach((cycle, i) => {
      tableRows.push({
        key: `old-${idx}-${i}`,
        cycleType: `Certification ${idx + 1} - Past Cycle ${i + 1}`,
        certificateNo: current.certificationNumber,
        company: current.companyName,
        registrationDate: cycle.registrationDateBefore,
        expiryDate: cycle.expiryDateBefore,
        type: "old",
        data: cycle,
      });
    });
  });

  // ✅ Expand logic
  const handleExpandRow = (record) => {
    if (record.type === "active") {
      const renewalAudits = [
        {
          auditType: "1st Surveillance",
          date: record.data?.firstSurveillanceAudit
            ? dayjs(record.data.firstSurveillanceAudit).format("DD-MM-YYYY")
            : "-",
          status: record.data?.firstSurveillanceStatus,
          notes: record.data?.firstSurveillanceNotes,
        },
        {
          auditType: "2nd Surveillance",
          date: record.data?.secondSurveillanceAudit
            ? dayjs(record.data.secondSurveillanceAudit).format("DD-MM-YYYY")
            : "-",
          status: record.data?.secondSurveillanceStatus,
          notes: record.data?.secondSurveillanceNotes,
        },
      ];

      return (
        <AuditSection
          title="Current Certification – Surveillance Audits"
          audits={renewalAudits}
        />
      );
    }

    if (record.type === "old") {
      const oldAudits = [
        {
          auditType: "1st Surveillance",
          date: record.data.firstSurveillanceHistory?.date
            ? dayjs(record.data.firstSurveillanceHistory.date).format(
                "DD-MM-YYYY"
              )
            : "-",
          status: record.data.firstSurveillanceHistory?.status,
          notes: record.data.firstSurveillanceHistory?.notes,
        },
        {
          auditType: "2nd Surveillance",
          date: record.data.secondSurveillanceHistory?.date
            ? dayjs(record.data.secondSurveillanceHistory.date).format(
                "DD-MM-YYYY"
              )
            : "-",
          status: record.data.secondSurveillanceHistory?.status,
          notes: record.data.secondSurveillanceHistory?.notes,
        },
      ];

      return (
        <AuditSection title="Past Certification Cycle" audits={oldAudits} />
      );
    }
  };

  return loading ? (
    <Spin size="large" style={{ display: "block", margin: "auto" }} />
  ) : (
    <Table
      bordered
      pagination={false}
      dataSource={tableRows}
      expandable={{ expandedRowRender: handleExpandRow }}
      columns={[
        { title: "Cycle", dataIndex: "cycleType" },
        { title: "Certificate No", dataIndex: "certificateNo" },
        { title: "Company", dataIndex: "company" },
        {
          title: "Registration Date",
          dataIndex: "registrationDate",
          render: (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "-"),
        },
        {
          title: "Expiry Date",
          dataIndex: "expiryDate",
          render: (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "-"),
        },
      ]}
    />
  );
};

export default AuditsTable;
